import { Elements } from "@stripe/react-stripe-js";
import Helmet from "react-helmet";
import { Redirect, Route } from "react-router-dom";
import { Switch } from "react-router-dom";

import AcceptPatientInvitePage from "@/components/AcceptPatientInvitePage";
import AcceptResultsAccessLink from "@/components/AcceptResultsAccessLink";
import ApolloManager from "@/components/ApolloManager";
import AppStateProvider from "@/components/AppStateProvider";
import AuthProvider from "@/components/AuthProvider";
import AuthRoute from "@/components/AuthRoute";
import Box from "@/components/Box";
import OtpGate from "@/components/OtpGate";
import Error404Page from "@/components/Error404Page";
import Error500Page from "@/components/Error500Page";
import { InternalTextLink } from "@/components/Links";
import PatientsViewRedirect from "@/components/PatientsViewRedirect";
import PlatformProfileWelcomePage from "@/components/PlatformProfileWelcomePage";
import RedirectPractitioners from "@/components/RedirectPractitioners";
import RedirectToLoginWithNext from "@/components/RedirectToLoginWithNext";
import RedirectToNewCmpUrlFromOld from "@/components/RedirectToNewCmpUrlFromOld";
import RegenerusCookiePolicyPage from "@/components/RegenerusCookiePolicy";
import RegenerusPrivacyPolicyPage from "@/components/RegenerusPrivacyPolicy";
import RegenerusShippingPolicyPage from "@/components/RegenerusShipping";
import RegenerusTermsAndConditionsPage from "@/components/RegenerusTermsAndConditions";
import RegenerusTermsOfUsePage from "@/components/RegenerusTermsOfUse";
import RootRedirect from "@/components/RootRedirect";
import SignupCompletePage from "@/components/SignupCompletePage";
import SignupConfirmPage from "@/components/SignupConfirmPage";
import UserOnlyNotifications from "@/components/UserOnlyNotifications";
import VersionMismatchBanner from "@/components/VersionMismatchBanner";
import CustomerTestResults from "@/components/customerView/testResults";
import CustomerReportsRoutes from "@/components/customerView/reports";
import ExpertRoutes from "@/components/experts";
import OnLogin from "@/components/notifications/events/OnLogin";
import ResetPassword from "@/components/resetPassword";
import { ChangeOverTimeContextProvider } from "@/contexts/ChangeOverTime";
import ContextProviderManager from "@/contexts/ContextProviderManager";
import { LocalStorageContextProvider } from "@/contexts/LocalStorage";
import { LocalesProvider } from "@/contexts/Locale";
import { NotificationsProvider } from "@/contexts/Notification";
import { TestBrushConfigContextProvider } from "@/contexts/TestBrushConfig";
import { UserProfileProvider } from "@/contexts/UserProfileContext";
import { getStripePublishableKey } from "@/core/config";
import { theme } from "@/core/theme";
import {
  ACCEPT_RESULTS_ACCESS_REQUEST_PATTERN_URL,
  ERROR_404_URL,
  ERROR_500_URL,
  HOME_URL,
  LOGIN_URL,
  OLD_TEST_PATTERN_URL,
  OLD_TESTS_URL,
  ORDERS_URL,
  PARTNERS_URL,
  PATIENT_CHECKOUT_URL,
  PLATFORM_PROFILE_DIET_PREFERENCES_URL,
  PLATFORM_PROFILE_FORM_URL,
  PLATFORM_PROFILE_WELCOME_URL,
  REQUEST_PASSWORD_RESET_URL,
  SETTINGS_URL,
  SIGNUP_COMPLETE_URL,
  SIGNUP_CONFIRM_PATTERN_URL,
  SUPPLEMENT_PATTERN_URL,
  SUPPLEMENTS_URL,
  TEST_FAMILY_PATTERN_URL,
  TEST_PATTERN_URL,
  TESTS_URL,
  TEST_RESULTS_URL_BASE,
  SHOP_SUPPLEMENTS_URL,
  SHOP_SUPPLEMENT_PATTERN_URL,
  SHOP_TESTS_URL,
  SHOP_TEST_PRODUCT_PATTERN_URL,
  SHOP_REPORT_PRODUCT_PATTERN_URL,
  PATIENT_URL,
  EXPERTS_DETAIL_URL,
  ACCEPT_PATIENT_INVITE_PATTERN_URL,
  EXPERTS_ROOT_URL,
  CMP_ROOT_URL,
  CMP_SIGNIN_URL,
  EXPERTS_SUPPORTED_TESTS_URL,
  SHOP_URL,
  EXPERTS_SUPPORTED_TEST_URL,
  PROFILE_URL,
  REGENERUS_TERMS_AND_CONDITIONS_URL,
  REGENERUS_TERMS_OF_USE_URL,
  REGENERUS_PRIVACY_URL,
  REGENERUS_COOKIE_POLICY_URL,
  REGENERUS_SHIPPING_URL
} from "@/core/urls";
import { loadStripe } from "@stripe/stripe-js";
import { ThemeProvider } from "styled-components";

import OrderRoutes from "../components/customerView/orders";
import PatientPayOrderPage from "../components/patientPay/PatientPayOrderPage";
import ProfilePage from "../components/customerView/profile/Profile";
import CustomerQuestionnaire from "../components/customerView/questionnaire/Questionnaire";
import { ModalProvider } from "../contexts/modal";
import DietPreferences from "./DietPreferences";
import LoggedOutRoute from "./LoggedOutRoute";
import Login from "./Login";
import Partners from "./Partners";
import PatientProfilePage from "./PatientProfilePage";
import SentryErrorTest from "./SentryErrorTest";
import "./styles.css";

const stripePromise = loadStripe(getStripePublishableKey());

function App() {
  return (
    <ThemeProvider theme={theme}>
      <AuthProvider>
        <ApolloManager>
          <OtpGate>
          <Elements stripe={stripePromise}>
            <ModalProvider>
              <ContextProviderManager
                providers={[
                  AppStateProvider,
                  LocalStorageContextProvider,
                  UserProfileProvider,
                  LocalesProvider,
                  ChangeOverTimeContextProvider,
                  TestBrushConfigContextProvider,
                  NotificationsProvider
                ]}
              >
                <Helmet>
                  <meta property="og:title" content="Regenerus" />
                  <meta
                    property="og:description"
                    content="The Omnos programme has been developed to identify how small adjustments to your lifestyle, nutrition and fitness can have incredible results on your overall wellbeing."
                  />
                  <meta property="og:image" content="/og_1200x630.jpg" />
                  <meta property="og:url" content="regeneruslabs.me" />
                  <meta name="twitter:card" content="summary_large_image" />
                </Helmet>
                <OnLogin />
                <UserOnlyNotifications />
                <VersionMismatchBanner />
                <Switch>
                  {/* Accounts */}
                  <AuthRoute path={HOME_URL} component={RootRedirect} exact />
                  <Route path={SIGNUP_CONFIRM_PATTERN_URL} component={SignupConfirmPage} exact />
                  <Route
                    path={ACCEPT_PATIENT_INVITE_PATTERN_URL}
                    component={AcceptPatientInvitePage}
                    exact
                  />
                  <Route path={SIGNUP_COMPLETE_URL} component={SignupCompletePage} exact />
                  <LoggedOutRoute path={LOGIN_URL} exact>
                    <Login
                      bottomLinks={
                        <>
                          <InternalTextLink href={REQUEST_PASSWORD_RESET_URL}>
                            Forgotten your password?
                          </InternalTextLink>
                          <Box fontSize={[14, 14, 16]} fontFamily="gilroyMedium" textAlign="center">
                            Please contact your practitioner if you would like to create a Patient
                            account with Regenerus.
                          </Box>
                        </>
                      }
                    />
                  </LoggedOutRoute>
                  <Route path="/accounts/password-reset" component={ResetPassword} />
                  <Redirect from={SETTINGS_URL} to={PROFILE_URL} />
                  <AuthRoute path={PROFILE_URL} component={ProfilePage} exact />
                  <AuthRoute path="/questionnaire" exact component={CustomerQuestionnaire} />
                  <AuthRoute path="/reports" component={CustomerReportsRoutes} />
                  {/* Invites */}
                  <AuthRoute
                    path={ACCEPT_RESULTS_ACCESS_REQUEST_PATTERN_URL}
                    component={AcceptResultsAccessLink}
                    exact
                  />
                  {/* Onboarding */}
                  <AuthRoute
                    path={PLATFORM_PROFILE_WELCOME_URL}
                    component={PlatformProfileWelcomePage}
                    exact
                  />
                  <AuthRoute
                    path={PLATFORM_PROFILE_FORM_URL}
                    component={PatientProfilePage}
                    exact
                  />
                  <AuthRoute
                    path={PLATFORM_PROFILE_DIET_PREFERENCES_URL}
                    component={DietPreferences}
                    exact
                  />

                  {/* Shop */}
                  <Redirect from={SHOP_SUPPLEMENTS_URL} to={ERROR_404_URL} />
                  <Redirect from={SHOP_TEST_PRODUCT_PATTERN_URL} to={EXPERTS_SUPPORTED_TEST_URL} />
                  <Route path={SHOP_REPORT_PRODUCT_PATTERN_URL} exact>
                    <RedirectPractitioners tab="reports">
                      <RedirectToLoginWithNext loginUrl={CMP_SIGNIN_URL}>
                        <Redirect to={PROFILE_URL} />
                      </RedirectToLoginWithNext>
                    </RedirectPractitioners>
                  </Route>
                  <Redirect from={SHOP_TESTS_URL} to={EXPERTS_SUPPORTED_TESTS_URL} />
                  <Redirect from={SHOP_URL} to={EXPERTS_SUPPORTED_TESTS_URL} />

                  <Redirect from={SUPPLEMENTS_URL} to={SHOP_SUPPLEMENTS_URL} exact />
                  <Redirect from={SUPPLEMENT_PATTERN_URL} to={SHOP_SUPPLEMENT_PATTERN_URL} exact />
                  <Redirect from={TESTS_URL} to={SHOP_TESTS_URL} exact />
                  <Redirect from={TEST_PATTERN_URL} to={SHOP_TEST_PRODUCT_PATTERN_URL} exact />
                  <Redirect from={TEST_FAMILY_PATTERN_URL} to={SHOP_TESTS_URL} exact />
                  <Redirect from={OLD_TESTS_URL} to={SHOP_TESTS_URL} exact />
                  <Redirect from={OLD_TEST_PATTERN_URL} to={SHOP_TEST_PRODUCT_PATTERN_URL} exact />
                  {/* End of shop */}

                  <Redirect from="/experts/:id" to={EXPERTS_DETAIL_URL} />
                  <Redirect from="/experts" to={EXPERTS_ROOT_URL} />
                  <Route path={EXPERTS_ROOT_URL} component={ExpertRoutes} />

                  <Route path={ORDERS_URL} component={OrderRoutes} />
                  <Route path={PATIENT_CHECKOUT_URL} component={PatientPayOrderPage} exact />

                  {/* Partners */}
                  <Route path={PARTNERS_URL} component={RedirectToNewCmpUrlFromOld} />
                  <Route path={CMP_ROOT_URL} component={Partners} />
                  <AuthRoute path={PATIENT_URL} component={PatientsViewRedirect} />
                  {/* Legal */}
                  <Route path={REGENERUS_TERMS_AND_CONDITIONS_URL} exact>
                    <Box />
                    <RegenerusTermsAndConditionsPage />
                  </Route>
                  <Route path={REGENERUS_TERMS_OF_USE_URL} exact>
                    <RegenerusTermsOfUsePage />
                  </Route>
                  <Route path={REGENERUS_SHIPPING_URL} exact>
                    <RegenerusShippingPolicyPage />
                  </Route>
                  <Route path={REGENERUS_PRIVACY_URL} exact>
                    <RegenerusPrivacyPolicyPage />
                  </Route>
                  <Route path={REGENERUS_COOKIE_POLICY_URL} exact>
                    <RegenerusCookiePolicyPage />
                  </Route>

                  {/* Errors */}
                  <Route path={ERROR_500_URL} exact component={Error500Page} />
                  <Route path={ERROR_404_URL} exact component={Error404Page} />
                  <AuthRoute path={TEST_RESULTS_URL_BASE} component={CustomerTestResults} />
                  <Redirect to={SETTINGS_URL} />
                  <Route path="/sentry-error-test" component={SentryErrorTest} exact />
                </Switch>
              </ContextProviderManager>
            </ModalProvider>
          </Elements>
          </OtpGate>
        </ApolloManager>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
